"use client";

import React, { useEffect, useId, useRef, useState } from "react";

export type ThemedSelectOption = Readonly<{
  value: string;
  label: string;
  disabled?: boolean;
}>;

type SharedProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "value" | "onChange" | "children" | "defaultValue"> & {
  options?: readonly ThemedSelectOption[];
  /** Existing option markup is accepted for incremental adoption; it is never rendered as a native dropdown. */
  children?: React.ReactNode;
  placeholder?: string;
  ariaLabel?: string;
  searchPlaceholder?: string;
  required?: boolean;
  triggerLabel?: string;
  /** Compact density for aligned calendar/filter toolbars; form controls keep their default size. */
  density?: "default" | "compact";
};
export type ThemedSelectProps = SharedProps & (
  | { multiple?: false; value: string; onChange: (value: string) => void }
  | { multiple: true; value: readonly string[]; onChange: (value: string[]) => void }
);

function textContent(children: React.ReactNode): string {
  return React.Children.toArray(children).map((child): string => {
    if (typeof child === "string" || typeof child === "number") return String(child);
    if (React.isValidElement<{ children?: React.ReactNode }>(child)) return textContent(child.props.children);
    return "";
  }).join("");
}
function optionsFromChildren(children: React.ReactNode, inheritedDisabled = false): ThemedSelectOption[] {
  return React.Children.toArray(children).flatMap((child): ThemedSelectOption[] => {
    if (!React.isValidElement<React.OptionHTMLAttributes<HTMLOptionElement>>(child)) return [];
    if (child.type === "option") {
      const label = child.props.label ?? textContent(child.props.children);
      return [{ value: String(child.props.value ?? label), label, disabled: inheritedDisabled || child.props.disabled }];
    }
    return optionsFromChildren(child.props.children, inheritedDisabled || child.props.disabled);
  });
}

/** One themed, accessible value selector for app forms, filters and shared profile composers. */
export function ThemedSelect(props: ThemedSelectProps) {
  const {
    value, onChange: _onChange, options: suppliedOptions, children, placeholder = "", ariaLabel,
    searchPlaceholder, required, triggerLabel, multiple, className, disabled, name, density = "default", ...buttonProps
  } = props;
  const options = suppliedOptions ?? optionsFromChildren(children);
  const selectedValues = typeof value === "string" ? [value] : value;
  const selectedLabel = options.filter((option) => selectedValues.includes(option.value)).map((option) => option.label).join(", ");
  const id = useId();
  const listId = `${id}-options`;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [placement, setPlacement] = useState<React.CSSProperties>({});
  const pendingFocus = useRef<"first" | "last" | "selected">("selected");
  const typeahead = useRef({ text: "", time: 0 });
  const visibleOptions = searchPlaceholder && search.trim()
    ? options.filter((option) => option.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()))
    : options;

  const close = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };
  const show = (focus: typeof pendingFocus.current = "selected") => {
    if (triggerRef.current?.matches(":disabled")) return;
    pendingFocus.current = focus;
    setSearch("");
    setOpen(true);
  };
  const choose = (option: ThemedSelectOption) => {
    if (option.disabled || triggerRef.current?.matches(":disabled")) return;
    if (props.multiple) {
      props.onChange(selectedValues.includes(option.value)
        ? selectedValues.filter((item) => item !== option.value)
        : [...selectedValues, option.value]);
    } else {
      props.onChange(option.value);
      close(true);
    }
  };

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const width = Math.min(Math.max(rect.width, 160), window.innerWidth - 16);
      const top = below < Math.min(340, menuRef.current?.scrollHeight ?? 340) && above > below;
      setPlacement({
        position: "fixed", width, left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        maxHeight: Math.max(48, Math.min(340, top ? above : below)),
        ...(top ? { bottom: window.innerHeight - rect.top + 6 } : { top: rect.bottom + 6 }),
      });
    };
    updatePosition();
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onScroll = (event: Event) => {
      if (!menuRef.current?.contains(event.target as Node)) updatePosition();
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", onScroll, true);
    const buttons = menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)');
    if (searchPlaceholder) searchRef.current?.focus();
    else if (buttons?.length) {
      const selected = Array.from(buttons).find((button) => button.getAttribute("aria-selected") === "true");
      const target = pendingFocus.current === "last" ? buttons[buttons.length - 1]
        : pendingFocus.current === "selected" ? selected ?? buttons[0] : buttons[0];
      target.focus();
    }
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, searchPlaceholder]);

  return (
    <div
      className={open ? "themed-select is-open" : "themed-select"}
      ref={rootRef}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) close();
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.preventDefault();
          event.stopPropagation();
          close(true);
          return;
        }
        if (event.key === "Tab" && open) {
          // Continue normal tab order from the field, not from a disappearing option.
          triggerRef.current?.focus();
          close();
          return;
        }
        if (!open) {
          if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
            event.preventDefault();
            show(event.key === "ArrowUp" || event.key === "End" ? "last" : "first");
          }
          return;
        }
        const buttons = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)') ?? []);
        if (!buttons.length) return;
        const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
        if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
          event.preventDefault();
          const next = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1
            : event.key === "ArrowDown" ? (index + 1) % buttons.length
              : (index <= 0 ? buttons.length : index) - 1;
          buttons[next].focus();
        } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey && document.activeElement !== searchRef.current && event.key !== " ") {
          const now = Date.now();
          typeahead.current = { text: now - typeahead.current.time < 600 ? typeahead.current.text + event.key : event.key, time: now };
          const query = typeahead.current.text.toLocaleLowerCase();
          const next = [...buttons.slice(index + 1), ...buttons.slice(0, index + 1)].find((button) => button.textContent?.trim().toLocaleLowerCase().startsWith(query));
          if (next) { event.preventDefault(); next.focus(); }
        }
      }}
    >
      <button
        {...buttonProps}
        id={buttonProps.id ?? id}
        ref={triggerRef}
        type="button"
        value={typeof value === "string" ? value : value.join(",")}
        className={["themed-select-trigger", density === "compact" && "themed-select-trigger--compact", className, open && "is-open"].filter(Boolean).join(" ")}
        role="combobox"
        aria-label={ariaLabel ?? buttonProps["aria-label"]}
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-required={required || undefined}
        disabled={disabled}
        onClick={() => open ? close(true) : show()}
      >
        <span className={selectedLabel || triggerLabel ? "" : "themed-select-placeholder"}>{triggerLabel ?? (selectedLabel || placeholder)}</span>
        <svg className="themed-select-chevron" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {name || required ? (
        <select
          hidden aria-hidden="true" tabIndex={-1} name={name} required={required} disabled={disabled}
          multiple={multiple} value={value as string | string[]} onChange={() => undefined}
          onInvalid={(event) => { event.preventDefault(); triggerRef.current?.focus(); }}
        >
          {options.map((option) => <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>)}
        </select>
      ) : null}
      {open ? (
        <div ref={menuRef} className="themed-select-menu" style={placement}>
          {searchPlaceholder ? (
            <input ref={searchRef} className="themed-select-search" type="search" aria-label={searchPlaceholder} placeholder={searchPlaceholder}
              value={search} onChange={(event) => setSearch(event.target.value)} />
          ) : null}
          <div id={listId} role="listbox" aria-label={ariaLabel ?? buttonProps["aria-label"]}
            aria-labelledby={ariaLabel || buttonProps["aria-label"] ? undefined : buttonProps["aria-labelledby"] ?? buttonProps.id ?? id}
            aria-multiselectable={multiple || undefined}>
            {visibleOptions.map((option) => {
              const selected = selectedValues.includes(option.value);
              return (
                <button key={option.value} type="button" value={option.value} role="option" aria-selected={selected} disabled={option.disabled}
                  className={selected ? "themed-select-option is-selected" : "themed-select-option"}
                  onClick={(event) => { event.preventDefault(); choose(option); }}>
                  <span>{option.label}</span>{selected ? <span className="themed-select-check" aria-hidden="true">✓</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
